/**
 * Shared client runtime: socket lifecycle, offline queue, DOM + format helpers.
 * Vanilla ES module — every surface (console, overlays, director, live) uses this.
 */

// ---------------------------------------------------------------------------
// Tiny DOM helpers
// ---------------------------------------------------------------------------

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export const LEAGUE_BRANDS = Object.freeze({
  ft20: Object.freeze({
    id: 'ft20',
    label: 'FT20',
    aliases: Object.freeze(['ft20']),
    logoSrc: '/client/assets/ft20-logo.png',
    teamLogoManifestPath: '/client/assets/team-logos/ft20-2026/manifest.json',
    teamLogoAssetBase: '/client/assets/team-logos/ft20-2026',
  }),
  fwcwl: Object.freeze({
    id: 'fwcwl',
    label: 'FWCWL',
    aliases: Object.freeze(['fwcwl']),
    logoSrc: '/client/assets/fwcwl-logo.png',
    teamLogoManifestPath: '/client/assets/team-logos/fwcwl/manifest.json',
    teamLogoAssetBase: '/client/assets/team-logos/fwcwl',
  }),
});

function explicitBrandFromTheme(theme, fallback = null) {
  if (!theme || typeof theme !== 'object') return null;
  const id = String(theme.id || theme.key || theme.slug || fallback?.id || '').trim().toLowerCase();
  if (!id && !theme.label && !theme.logoSrc && !theme.tokens) return null;
  return {
    ...(fallback || LEAGUE_BRANDS.ft20),
    id: id || fallback?.id || 'custom',
    label: String(theme.label || theme.name || fallback?.label || id || 'League').trim(),
    logoSrc: theme.logoSrc || theme.logoUrl || fallback?.logoSrc || '/client/icons/icat-icon-192.png',
    teamLogoManifestPath: theme.teamLogoManifestPath || fallback?.teamLogoManifestPath || LEAGUE_BRANDS.ft20.teamLogoManifestPath,
    teamLogoAssetBase: theme.teamLogoAssetBase || fallback?.teamLogoAssetBase || LEAGUE_BRANDS.ft20.teamLogoAssetBase,
    tokens: theme.tokens || theme.cssVars || fallback?.tokens || null,
  };
}

export function resolveLeagueBrand(...sources) {
  const raw = sources.flat(Infinity).filter(Boolean).join(' ').toLowerCase();
  return Object.values(LEAGUE_BRANDS).find((brand) => brand.aliases.some((alias) => raw.includes(alias)))
    || LEAGUE_BRANDS.ft20;
}

export function resolveScopedBrand({ match = null, season = null, league = null, fallbackSources = [] } = {}) {
  const inheritedLeague = explicitBrandFromTheme(league?.theme, resolveLeagueBrand(league?.shortName, league?.name, league?.id, fallbackSources));
  const leagueBrand = inheritedLeague || resolveLeagueBrand(league?.shortName, league?.name, league?.id, fallbackSources);
  const seasonBrand = explicitBrandFromTheme(season?.theme, leagueBrand);
  const matchBrand = explicitBrandFromTheme(match?.theme, seasonBrand || leagueBrand);
  return matchBrand
    || seasonBrand
    || leagueBrand
    || resolveLeagueBrand(
      match?.label,
      season?.shortName,
      season?.name,
      season?.id,
      league?.shortName,
      league?.name,
      league?.id,
      fallbackSources,
    );
}

export function applyBrandTokens(target, brand) {
  const el = target || (typeof document === 'undefined' ? null : document.body);
  if (!el || !brand) return;
  el.dataset.theme = brand.id;
  for (const [key, value] of Object.entries(brand.tokens || {})) {
    if (!/^--[a-z0-9-]+$/i.test(key)) continue;
    el.style.setProperty(key, String(value));
  }
}

export function applyScopedTheme(scope = {}) {
  if (typeof document === 'undefined') return resolveScopedBrand(scope);
  const brand = resolveScopedBrand(scope);
  applyBrandTokens(document.documentElement, brand);
  applyBrandTokens(document.body, brand);
  document.documentElement.classList.add('outdoor-ui');
  document.body.classList.add('match-themed');
  const themed = getComputedStyle(document.body).getPropertyValue('--theme-color').trim()
    || getComputedStyle(document.documentElement).getPropertyValue('--theme-color').trim();
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta && themed) themeMeta.setAttribute('content', themed);
  return brand;
}

export function applyMatchBranding(branding = {}, match = null) {
  const clean = applyBranding(branding);
  const scope = match?.brandingScope || {};
  const brand = applyScopedTheme({
    match: scope.match || null,
    season: scope.season || {
      id: match?.seasonId,
      name: match?.seasonName,
      shortName: match?.seasonShortName,
    },
    league: scope.league || {
      id: match?.leagueId,
      name: match?.leagueName,
      shortName: match?.leagueShortName,
    },
    fallbackSources: [
      clean.shortName,
      clean.orgName,
      match?.seasonShortName,
      match?.seasonName,
      match?.leagueShortName,
      match?.leagueName,
    ],
  });
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('match-themed');
    body.classList.add('match-themed');
    const vars = {};
    if (clean.primary) vars['--primary'] = clean.primary;
    if (clean.accent) {
      vars['--accent'] = clean.accent;
      vars['--live'] = clean.accent;
    }
    if (clean.themeColor) vars['--theme-color'] = clean.themeColor;
    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
      body.style.setProperty(key, value);
    }
    const themed = getComputedStyle(body).getPropertyValue('--theme-color').trim() || clean.themeColor;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta && themed) themeMeta.setAttribute('content', themed);
  }
  return { branding: clean, brand };
}

export function loadTeamLogoManifest(brand = LEAGUE_BRANDS.ft20) {
  return fetch(brand.teamLogoManifestPath)
    .then((res) => (res.ok ? res.json() : { teams: [] }))
    .catch(() => ({ teams: [] }));
}

export function teamLogoAssetUrl(team, manifest, brand = LEAGUE_BRANDS.ft20) {
  const shortName = String(team?.short || team?.shortName || '').trim().toUpperCase();
  const entry = (manifest?.teams || []).find((candidate) => String(candidate.shortName || '').toUpperCase() === shortName);
  return entry?.status === 'available'
    ? entry.publicAsset || `${brand.teamLogoAssetBase}/tm-${shortName.toLowerCase()}.jpg`
    : '';
}

/** h('div', {class:'x', onclick}, child, …) — null/false children are skipped. */
export function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v === null || v === undefined || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
    else if (k === 'class') el.className = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else el.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children.flat(Infinity)) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c.nodeType ? c : document.createTextNode(String(c)));
  }
  return el;
}

export function screenGuide({
  title = 'Start here',
  who = '',
  what = '',
  when = '',
  where = '',
  why = '',
  how = '',
  action = null,
  tone = '',
} = {}) {
  const facts = [what, when, where].filter((value) => value !== null && value !== undefined && value !== '');
  const aria = [title, who, what, when, where, why, how]
    .filter((value) => value !== null && value !== undefined && value !== '')
    .join('. ');

  return h('section', { class: `screen-guide ${tone}`.trim() },
    h('div', { class: 'screen-guide-focus' },
      h('span', { class: 'eyebrow' }, title),
      h('strong', {}, how || 'Choose the next safe action.'),
      action ? h('div', { class: 'screen-guide-action' }, action) : null),
    facts.length ? h('div', { class: 'screen-guide-facts', 'aria-label': aria },
      facts.map((value) => h('span', { class: 'screen-guide-chip' }, value))) : null);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const fmt = {
  overs: (balls) => `${Math.floor(balls / 6)}.${balls % 6}`,
  figures: (b) => `${Math.floor(b.balls / 6)}.${b.balls % 6}-${b.maidens}-${b.runs}-${b.wickets}`,
  batLine: (b) => `${b.runs}(${b.balls})`,
  num1: (x) => (x === null || x === undefined || Number.isNaN(x) ? '—' : (Math.round(x * 10) / 10).toFixed(1)),
  score: (inn) => `${inn.runs}/${inn.wickets}`,
};

/** Scale a fixed 1920×1080 #stage to fit the viewport (OBS is exactly 1:1). */
export function fitStage(stage = document.getElementById('stage')) {
  if (!stage) return;
  const fit = () => {
    const s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
    stage.style.transformOrigin = 'top left';
    stage.style.transform = `scale(${s})`;
  };
  fit();
  window.addEventListener('resize', fit);
}

// ---------------------------------------------------------------------------
// Socket runtime
// ---------------------------------------------------------------------------

function ensureIO(origin = '') {
  if (window.io) return Promise.resolve(window.io);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${String(origin || '').replace(/\/$/, '')}/socket.io/socket.io.js`;
    s.onload = () => (window.io ? resolve(window.io) : reject(new Error('socket.io client is unavailable')));
    s.onerror = () => reject(new Error('socket.io client failed to load'));
    document.head.appendChild(s);
  });
}

function clientId() {
  let id = localStorage.getItem('icat-client-id');
  if (!id) {
    id = `c-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem('icat-client-id', id);
  }
  return id;
}

function scorerSessionId() {
  try {
    let id = sessionStorage.getItem('icat-scorer-session-id');
    if (!id) {
      id = `sc-${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      sessionStorage.setItem('icat-scorer-session-id', id);
    }
    return id;
  } catch {
    return clientId();
  }
}

const rid = () => `e-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

export function isNewerSnapshotVersion(snapshotVersion, currentVersion) {
  return Number(snapshotVersion) > Number(currentVersion);
}

export function completedOverSummary(state, inningIndex, over) {
  const inn = state?.innings?.[inningIndex];
  const overRow = inn?.overByOver?.find((row) => row.over === over);
  if (!inn || !overRow) return null;
  const completed = inn.overByOver.filter((row) => row.over <= over);
  return {
    runs: completed.reduce((total, row) => total + Number(row.runs || 0), 0),
    wickets: completed.reduce((total, row) => total + Number(row.wickets || 0), 0),
    overRuns: Number(overRow.runs || 0),
    overWickets: Number(overRow.wickets || 0),
  };
}

async function fetchInfo() {
  try { return await (await fetch('/api/info')).json(); } catch { return {}; }
}

function connectHttp(opts = {}) {
  const cb = (name, ...args) => { try { opts[name]?.(...args); } catch (error) { console.error(error); } };
  const app = {
    matchId: opts.matchId || null,
    role: 'view', state: null, match: null, presentation: null, branding: null,
    productionSponsors: [], streamingPolicy: null, version: 0, rev: 0,
    online: false, queue: [], queueFrozen: false,
  };
  let stopped = false;
  let pollTimer = null;
  let pin = opts.pin || '';
  const scorerClientId = opts.role === 'scorer' ? scorerSessionId() : clientId();
  let leaseToken = '';
  let leaseHeartbeatAt = 0;
  let joinOptions = { takeover: !!opts.takeover };
  const queueKey = () => `icat-queue-${app.matchId}`;
  const loadQueue = () => { try { app.queue = JSON.parse(localStorage.getItem(queueKey()) || '[]'); } catch { app.queue = []; } };
  const saveQueue = () => localStorage.setItem(queueKey(), JSON.stringify(app.queue));
  app.socket = { disconnect() { stopped = true; clearTimeout(pollTimer); } };

  async function call(path, body) {
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-icat-pin': pin },
        body: JSON.stringify({ ...body, pin, clientId: scorerClientId, leaseToken }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok && !result.error && !result.errors) result.errors = [`Request failed (${response.status})`];
      return result;
    } catch {
      return { ok: false, errors: ['offline'], timeout: true };
    }
  }

  const noteRevoked = (result) => {
    if (result?.revoked) {
      app.role = 'view';
      app.queueFrozen = true;
      cb('onStatus', 'revoked', result);
    }
    return result;
  };

  const enqueue = (event, force) => {
    app.queue.push({ event, force, baseVersion: app.version + app.queue.length });
    saveQueue();
    cb('onQueue', app.queue.length);
    return { ok: true, queued: true };
  };

  let flushing = false;
  async function flush() {
    if (flushing || !app.online || app.queueFrozen || !app.queue.length) return;
    flushing = true;
    try {
      while (app.queue.length && app.online && !app.queueFrozen) {
        const item = app.queue[0];
        const result = await call(`/api/matches/${encodeURIComponent(app.matchId)}/events`, {
          event: item.event, force: item.force, expectedVersion: item.baseVersion,
        });
        if (result.timeout) { app.online = false; cb('onStatus', 'offline'); break; }
        if (result.versionConflict) { app.queueFrozen = true; cb('onQueueConflict', app.queue.slice()); break; }
        app.queue.shift();
        saveQueue();
        if (!result.ok && !result.duplicate) cb('onQueueDrop', item.event, result.errors || result.warnings || []);
      }
    } finally {
      flushing = false;
      cb('onQueue', app.queue.length);
    }
  }

  async function refreshSnapshot() {
    if (stopped || !app.matchId) return;
    try {
      const response = await fetch(`/api/matches/${encodeURIComponent(app.matchId)}`, { headers: { accept: 'application/json' } });
      const snapshot = await response.json();
      // A poll may have started before a scoring write and finish after its
      // response. Event versions are monotonic, so never let that stale poll
      // roll the locally-applied scoreboard back.
      if (response.ok && isNewerSnapshotVersion(snapshot.version, app.version)) {
        app.version = Number(snapshot.version || 0);
        app.state = snapshot.state;
        app.match = snapshot.match || app.match;
        cb('onState', app.state, app.version);
      }
      if (!app.online) { app.online = true; cb('onStatus', 'online'); flush(); }
      if (app.role === 'scorer' && leaseToken && Date.now() - leaseHeartbeatAt >= 20_000) {
        leaseHeartbeatAt = Date.now();
        noteRevoked(await call(`/api/matches/${encodeURIComponent(app.matchId)}/lease`, {}));
      }
    } catch {
      if (app.online) { app.online = false; cb('onStatus', 'offline'); }
    }
  }

  function applyMutationSnapshot(result) {
    if (!result?.ok || !result.state) return false;
    app.version = Number(result.version || app.version || 0);
    app.rev = Number(result.rev || app.rev || 0);
    app.state = result.state;
    if (result.match) app.match = result.match;
    cb('onState', app.state, app.version);
    return true;
  }

  async function poll() {
    await refreshSnapshot();
    if (stopped) return;
    pollTimer = setTimeout(poll, 1500);
  }

  async function join() {
    cb('onStatus', 'connecting');
    if (!app.matchId) {
      const info = await fetchInfo();
      app.matchId = info.activeMatchId || null;
    }
    if (!app.matchId) { cb('onStatus', 'no-match'); return; }
    const result = await call(`/api/matches/${encodeURIComponent(app.matchId)}/join`, { role: opts.role || 'view', ...joinOptions });
    if (!result.ok) { cb('onStatus', result.error || 'join-failed', result); return; }
    app.role = result.role;
    leaseToken = result.leaseToken || '';
    leaseHeartbeatAt = Date.now();
    app.presentation = result.presentation;
    app.branding = result.branding || {};
    app.productionSponsors = result.productionSponsors || [];
    app.streamingPolicy = result.streamingPolicy || null;
    app.version = Number(result.version || 0);
    app.rev = Number(result.rev || 0);
    app.state = result.state;
    app.match = result.match || null;
    app.online = true;
    applyMatchBranding(app.branding, app.match);
    loadQueue();
    cb('onStatus', 'online');
    cb('onBranding', app.branding);
    cb('onPresentation', app.presentation);
    cb('onState', app.state, app.version);
    if (result.revoked) noteRevoked(result);
    flush();
    pollTimer = setTimeout(poll, 1500);
  }

  app.send = async (event, { force = false } = {}) => {
    const stamped = { id: rid(), ...event };
    if (!app.online) return enqueue(stamped, force);
    const result = await call(`/api/matches/${encodeURIComponent(app.matchId)}/events`, { event: stamped, force });
    if (result.timeout) return enqueue(stamped, force);
    if (result.ok && !applyMutationSnapshot(result)) await refreshSnapshot();
    return noteRevoked(result);
  };
  app.undo = async () => { const result = noteRevoked(await call(`/api/matches/${encodeURIComponent(app.matchId)}/undo`, {})); if (result.ok && !applyMutationSnapshot(result)) await refreshSnapshot(); return result; };
  app.edit = async (seq, event) => { const result = noteRevoked(await call(`/api/matches/${encodeURIComponent(app.matchId)}/edit`, { seq, event })); if (result.ok && !applyMutationSnapshot(result)) await refreshSnapshot(); return result; };
  app.queuedEvents = () => app.queue.slice();
  app.resendQueued = async (index) => {
    const item = app.queue[index];
    if (!item) return { ok: false, errors: ['no such queued ball'] };
    const result = await call(`/api/matches/${encodeURIComponent(app.matchId)}/events`, { event: item.event, force: true });
    if (result.ok || result.duplicate) { app.queue.splice(index, 1); saveQueue(); cb('onQueue', app.queue.length); }
    return result;
  };
  app.discardQueued = (index) => { app.queue.splice(index, 1); saveQueue(); cb('onQueue', app.queue.length); };
  app.unfreezeQueue = () => { app.queueFrozen = false; flush(); };
  app.setPresentation = async () => ({ ok: false, errors: ['presentation control requires the live production service'] });
  app.fire = async () => ({ ok: false, errors: ['manual effects require the live production service'] });
  app.rejoin = (extra = {}) => {
    if (extra.pin) pin = extra.pin;
    joinOptions = { ...joinOptions, ...extra };
    return join();
  };
  app.fetchEvents = async () => (await (await fetch(`/api/matches/${encodeURIComponent(app.matchId)}/events`)).json());
  queueMicrotask(join);
  return app;
}

/**
 * Connect to a match. Resolves once the first join succeeds (or immediately
 * for view roles with no match yet — it keeps polling and joins when one
 * appears). See docs/reference/contract.md §8.
 */
export async function connect(opts = {}) {
  const socketOrigin = String(opts.socketOrigin || '').replace(/\/$/, '');
  const io = await ensureIO(socketOrigin).catch(() => null);
  if (!io) return connectHttp(opts);
  const cid = clientId();
  const cb = (name, ...args) => { try { opts[name]?.(...args); } catch (e) { console.error(e); } };

  const app = {
    matchId: opts.matchId || null,
    role: 'view',
    state: null,
    match: null,
    presentation: null,
    branding: null,
    productionSponsors: [],
    streamingPolicy: null,
    version: 0,
    rev: 0,          // monotonic server change counter — ordering guard
    online: false,
    queue: [],
    queueFrozen: false, // stop auto-flush after a takeover/version conflict
  };

  const queueKey = () => `icat-queue-${app.matchId}`;
  const loadQueue = () => { try { app.queue = JSON.parse(localStorage.getItem(queueKey()) || '[]'); } catch { app.queue = []; } };
  const saveQueue = () => localStorage.setItem(queueKey(), JSON.stringify(app.queue));

  const socket = io(socketOrigin || undefined, { reconnectionDelayMax: 3000, withCredentials: true });
  app.socket = socket;

  const emitAck = (name, payload, timeoutMs = 8000) => new Promise((resolve) => {
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve({ ok: false, errors: ['timeout'], timeout: true }); } }, timeoutMs);
    socket.emit(name, payload, (res) => { if (!done) { done = true; clearTimeout(t); resolve(res || { ok: false, errors: ['no ack'] }); } });
  });

  let joinOpts = {
    role: opts.role || 'view',
    pin: opts.pin,
    takeover: opts.takeover,
    cameraSlot: opts.cameraSlot,
    sessionId: opts.sessionId,
    previewId: opts.previewId,
  };

  /** An ack carrying revoked:true means another device took this role over. */
  const noteRevoked = (res) => {
    if (res && res.revoked) {
      app.role = 'view';
      app.queueFrozen = true; // stale queued balls must never auto-apply later
      cb('onStatus', 'revoked');
    }
    return res;
  };

  async function join() {
    if (!app.matchId) {
      const info = await fetchInfo();
      app.matchId = opts.matchId || info.activeMatchId || null;
      if (!app.matchId) {
        cb('onStatus', 'no-match');
        setTimeout(join, 3000); // a match will appear; overlays just wait
        return;
      }
    }
    let dynamicJoin = {};
    if (typeof opts.beforeJoin === 'function') {
      try {
        dynamicJoin = await opts.beforeJoin({ matchId: app.matchId, joinOptions: { ...joinOpts } }) || {};
      } catch (error) {
        const failure = error?.result || { ok: false, error: error?.message || 'join-authorization-failed' };
        cb('onStatus', failure.error || 'join-authorization-failed', failure);
        return;
      }
    }
    const res = await emitAck('join', { matchId: app.matchId, clientId: cid, ...joinOpts, ...dynamicJoin });
    if (!res.ok) {
      cb('onStatus', res.error || 'join-failed', res); // res.retryInMs rides along on 'locked-out'
      if (res.error === 'no-match') { app.matchId = null; setTimeout(join, 3000); }
      return;
    }
    app.role = res.role;
    app.presentation = res.presentation;
    app.branding = res.branding;
    app.productionSponsors = Array.isArray(res.productionSponsors) ? res.productionSponsors : [];
    app.match = res.match || null;
    applyMatchBranding(app.branding, app.match);
    app.pins = res.pins;
    app.streaming = res.streaming;
    app.streamingPolicy = res.streamingPolicy || null;
    app.camera = res.camera || null;
    app.cameraSlot = res.cameraSlot || null;
    // a broadcast may have raced ahead of this snapshot — never regress (B6)
    if (!(res.rev !== undefined && res.rev < app.rev)) {
      app.version = res.version;
      app.rev = res.rev || 0;
      app.state = res.state;
    }
    app.online = true;
    cb('onStatus', 'online');
    cb('onBranding', app.branding);
    cb('onPresentation', app.presentation);
    cb('onState', app.state, app.version);
    loadQueue();
    flush();
  }

  let flushing = false;
  async function flush() {
    if (flushing || !app.online || app.role === 'view' || app.queueFrozen || !app.queue.length) return;
    flushing = true;
    try {
      while (app.queue.length && app.online && !app.queueFrozen) {
        const item = app.queue[0];
        // A2: expectedVersion = where the log stood when this ball was queued;
        // a mismatch means scoring continued elsewhere — stop and let the
        // scorer review instead of silently corrupting the innings.
        const res = await emitAck('append', {
          matchId: app.matchId, event: item.event, force: item.force,
          expectedVersion: item.baseVersion,
        });
        if (res.timeout) break; // still offline-ish; retry on next reconnect
        if (res.revoked) { noteRevoked(res); break; }
        if (res.versionConflict) {
          app.queueFrozen = true;
          cb('onQueueConflict', app.queue.slice());
          break;
        }
        app.queue.shift();
        saveQueue();
        if (!res.ok && !res.duplicate) cb('onQueueDrop', item.event, res.errors || res.warnings || []);
      }
    } finally {
      flushing = false;
      cb('onQueue', app.queue.length);
    }
  }

  socket.on('connect', join);
  socket.on('disconnect', () => { app.online = false; cb('onStatus', 'offline'); });
  socket.on('state', (msg) => {
    if (msg.matchId !== app.matchId) return;
    if (msg.rev !== undefined && msg.rev < app.rev) return; // out-of-order broadcast (B6)
    if (msg.rev !== undefined) app.rev = msg.rev;
    if (msg.match) app.match = msg.match;
    app.state = msg.state;
    app.version = msg.version;
    cb('onState', app.state, app.version);
  });
  socket.on('fx', (msg) => {
    if (msg.matchId !== app.matchId) return;
    if (opts.previewId && msg.context?.previewId !== opts.previewId) return;
    cb('onFx', msg.fx, !!msg.manual);
  });
  socket.on('production:moment', (msg) => {
    if (msg.matchId !== app.matchId) return;
    if (opts.previewId && msg.context?.previewId !== opts.previewId) return;
    cb('onProductionMoment', msg);
  });
  socket.on('presentation', (msg) => {
    if (msg.matchId !== app.matchId) return;
    app.presentation = msg.presentation;
    cb('onPresentation', app.presentation);
  });
  socket.on('branding', (b) => { app.branding = b; applyMatchBranding(b, app.match); cb('onBranding', b); });
  socket.on('scorer-revoked', (msg) => {
    if (msg.matchId !== app.matchId) return;
    app.role = 'view';
    app.queueFrozen = true;
    cb('onStatus', 'revoked');
  });
  socket.on('streamer-revoked', (msg) => {
    if (msg.matchId !== app.matchId) return;
    if (app.role === 'streamer') app.role = 'view';
    cb('onStatus', 'revoked', msg);
  });
  socket.on('stream:status', (msg) => { if (msg.matchId === app.matchId) cb('onStreamStatus', msg); });
  socket.on('stream:publisher-changed', (msg) => { if (msg.matchId === app.matchId) cb('onPublisherChanged', msg); });
  socket.on('camera:status', (msg) => {
    if (msg.matchId !== app.matchId) return;
    app.camera = msg;
    cb('onCameraStatus', msg);
  });
  socket.on('viewers', (msg) => { if (msg.matchId === app.matchId) cb('onViewers', msg.count); });

  // overlays follow the server's active match: when this one is done,
  // quietly watch for the next one
  if (!opts.matchId && (opts.follow ?? true)) {
    setInterval(async () => {
      if (!app.state || app.state.phase !== 'complete') return;
      const info = await fetchInfo();
      if (info.activeMatchId && info.activeMatchId !== app.matchId) {
        app.matchId = info.activeMatchId;
        join();
      }
    }, 10000);
  }

  const enqueue = (ev, force) => {
    // baseVersion = the log position this ball logically follows: current
    // version plus the balls already ahead of it in the queue (A2)
    app.queue.push({ event: ev, force, baseVersion: app.version + app.queue.length });
    saveQueue();
    cb('onQueue', app.queue.length);
    return { ok: true, queued: true };
  };

  /** Append an event. Offline (or timeout): queued + optimistic ack. */
  app.send = async (event, { force = false } = {}) => {
    const ev = { id: rid(), ...event };
    if (!app.online) return enqueue(ev, force);
    const res = await emitAck('append', { matchId: app.matchId, event: ev, force });
    if (res.timeout) return enqueue(ev, force);
    return noteRevoked(res);
  };

  app.undo = () => emitAck('undo', { matchId: app.matchId }).then(noteRevoked);
  app.edit = (seq, event) => emitAck('edit', { matchId: app.matchId, seq, event }).then(noteRevoked);

  // ---- offline-queue review (A2) — used by the console's conflict sheet ----
  app.queuedEvents = () => app.queue.slice();
  /** Re-apply one reviewed ball on top of the current log. */
  app.resendQueued = async (index) => {
    const item = app.queue[index];
    if (!item) return { ok: false, errors: ['no such queued ball'] };
    const res = await emitAck('append', { matchId: app.matchId, event: item.event, force: true });
    if (res.ok || res.duplicate) {
      app.queue.splice(index, 1);
      saveQueue();
      cb('onQueue', app.queue.length);
      if (!app.queue.length) app.queueFrozen = false;
    }
    return noteRevoked(res);
  };
  app.discardQueued = (index) => {
    app.queue.splice(index, 1);
    saveQueue();
    cb('onQueue', app.queue.length);
    if (!app.queue.length) app.queueFrozen = false;
  };
  app.unfreezeQueue = () => { app.queueFrozen = false; flush(); };
  app.setPresentation = (patch) => emitAck('presentation', { matchId: app.matchId, patch });
  app.fire = (fx) => emitAck('fire', { matchId: app.matchId, fx });
  app.rejoin = (extra = {}) => { joinOpts = { ...joinOpts, ...extra }; return join(); };
  app.fetchEvents = async () => (await (await fetch(`/api/matches/${app.matchId}/events`)).json());

  cb('onStatus', 'connecting');
  return app;
}

// ---------------------------------------------------------------------------
// Misc shared bits
// ---------------------------------------------------------------------------

export function teamOf(state, teamId) {
  return state.config.teams.find((t) => t.id === teamId);
}

export function playerName(state, pid) {
  if (!pid) return '';
  for (const t of ['A', 'B']) {
    const p = state.squads[t].find((x) => x.id === pid);
    if (p) return p.name;
  }
  return pid;
}

/** The innings the viewer cares about right now (live one, else latest). */
export function focusInnings(state) {
  if (!state || !state.innings.length) return null;
  return state.innings[state.innings.length - 1];
}

export const BRANDING_DEFAULTS = {
  appName: 'ICAT Cricket Live',
  shortName: 'ICAT Cricket',
  orgName: 'ICAT',
  footer: 'ICAT — Tampa, FL',
  logo: null,
  sponsors: [],
  accent: '#00a676',
  primary: '#155eef',
  themeColor: '#101820',
  outdoorMode: true,
};

function hex(value, fallback) {
  return /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : fallback;
}

export function normalizeBranding(branding = {}) {
  const merged = { ...BRANDING_DEFAULTS, ...(branding || {}) };
  return {
    ...merged,
    accent: hex(merged.accent, BRANDING_DEFAULTS.accent),
    primary: hex(merged.primary, BRANDING_DEFAULTS.primary),
    themeColor: hex(merged.themeColor, merged.primary || BRANDING_DEFAULTS.themeColor),
    outdoorMode: merged.outdoorMode !== false,
    sponsors: Array.isArray(merged.sponsors) ? merged.sponsors.slice(0, 3) : [],
  };
}

export function applyBranding(branding) {
  const clean = normalizeBranding(branding);
  const root = document.documentElement;
  root.style.setProperty('--accent', clean.accent);
  root.style.setProperty('--primary', clean.primary);
  root.style.setProperty('--theme-color', clean.themeColor);
  root.classList.toggle('outdoor-ui', clean.outdoorMode);
  root.dataset.brand = clean.shortName || clean.orgName || 'ICAT';

  const themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute('content', clean.themeColor);
  const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitle) appleTitle.setAttribute('content', clean.shortName);
  return clean;
}
